import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "../components/ui/button";
import { Card, CardTitle } from "../components/ui/card";
import { useAuthorisation } from "../hooks/useAuthorisation";

function P2PSellerDashboard() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("ALL"); // ALL, DRAFT, PUBLISHED
  const { getAccessTokenOrSignIn } = useAuthorisation();

  useEffect(() => {
    // Fetch seller's listings from API
    const fetchSellerListings = async () => {
      setIsLoading(true);
      setError(null);
      try {
        console.log("Fetching seller listings from API...");

        // Get authentication token
        const accessToken = await getAccessTokenOrSignIn();
        if (!accessToken) {
          throw new Error("Authentication required");
        }

        // Get API base URL from environment
        const apiBaseUrl =
          process.env.REACT_APP_API_BASE_URL || "";

        // Use user_id=me to filter by authenticated user (shows all statuses)
        const response = await fetch(
          `${apiBaseUrl}/api/secure/p2p/listings?user_id=me`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || "Failed to fetch listings");
        }

        console.log(`Fetched ${data.listings.length} seller listings`);
        setListings(data.listings);
      } catch (err) {
        console.error("Error fetching seller listings:", err);
        setError(
          err.message || "Failed to load your listings. Please try again later."
        );
      }
      setIsLoading(false);
    };
    fetchSellerListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter listings based on selected filter
  const filteredListings = listings.filter((listing) => {
    if (filter === "ALL") return true;
    return listing.status === filter;
  });

  const handleNewListing = () => {
    navigate("/sell");
  };

  const handleEditListing = (listingId) => {
    console.log("Edit listing:", listingId);
    // TODO: Implement edit functionality
    alert(`Edit functionality for listing ${listingId} coming soon!`);
  };

  const handleViewListing = (listingId) => {
    navigate(`/p2p/listing/${listingId}`);
  };

  return (
    <div className="pt-20 px-4 pb-4 md:pt-24 md:px-8 md:pb-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">My Listings</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/p2p/offers")}>
            Manage Offers
          </Button>
          <Button onClick={handleNewListing}>Create New Listing</Button>
        </div>
      </div>

      {/* Filter buttons */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={filter === "ALL" ? "default" : "outline"}
          onClick={() => setFilter("ALL")}
        >
          All ({listings.length})
        </Button>
        <Button
          variant={filter === "DRAFT" ? "default" : "outline"}
          onClick={() => setFilter("DRAFT")}
        >
          Drafts ({listings.filter((l) => l.status === "DRAFT").length})
        </Button>
        <Button
          variant={filter === "PUBLISHED" ? "default" : "outline"}
          onClick={() => setFilter("PUBLISHED")}
        >
          Published ({listings.filter((l) => l.status === "PUBLISHED").length})
        </Button>
      </div>

      {isLoading && <p>Loading your listings...</p>}
      {error && <p className="text-red-500">Error: {error}</p>}

      {!isLoading && !error && filteredListings.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600 mb-4">
            {filter === "ALL"
              ? "You don't have any listings yet."
              : `No ${filter.toLowerCase()} listings.`}
          </p>
          {filter === "ALL" && (
            <Button onClick={handleNewListing}>
              Create Your First Listing
            </Button>
          )}
        </div>
      )}

      {!isLoading && !error && filteredListings.length > 0 && (
        <div className="grid grid-cols-1 gap-4">
          {filteredListings.map((listing) => (
            <Card
              key={listing.id}
              className="overflow-hidden border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex flex-col md:flex-row">
                {/* Image */}
                <div className="w-full md:w-48 h-48 bg-gray-100 flex-shrink-0">
                  <img
                    src={
                      listing.images?.[0] || "https://via.placeholder.com/300"
                    }
                    alt={listing.title}
                    className="object-cover w-full h-full"
                  />
                </div>

                {/* Content */}
                <div className="flex-1 p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-1">
                        {listing.title || "Untitled"}
                      </CardTitle>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            listing.status === "PUBLISHED"
                              ? "bg-green-100 text-green-800"
                              : listing.status === "DRAFT"
                                ? "bg-gray-100 text-gray-800"
                                : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {listing.status}
                        </span>
                        {listing.condition && (
                          <span>• {listing.condition}</span>
                        )}
                      </div>
                    </div>
                    <p className="text-xl font-semibold">
                      ${listing.price?.toFixed(2) || "N/A"}
                    </p>
                  </div>

                  {listing.description && (
                    <p className="text-sm text-gray-700 mb-4 line-clamp-2">
                      {listing.description}
                    </p>
                  )}

                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    {listing.brand && (
                      <span className="font-medium">{listing.brand}</span>
                    )}
                    {listing.category && <span>• {listing.category}</span>}
                  </div>

                  {/* View count - only show for published listings */}
                  {listing.status === "PUBLISHED" && (
                    <div className="flex items-center gap-1 text-sm text-gray-500 mt-2">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      <span>{listing.viewCount || 0} views</span>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewListing(listing.id)}
                    >
                      View
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditListing(listing.id)}
                    >
                      Edit
                    </Button>
                    {/* TODO: Add more actions (delete, republish, etc.) */}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default P2PSellerDashboard;
